import { on } from '@ember/modifier';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, next } from '@ember/runloop';
import { tracked } from '@glimmer/tracking';

import { SheetColumnType } from 'webapp/enums/enums';
import SheetSelection from 'webapp/utils/sheet-selection';

import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import CustomQuerySetupSheetLoaderComponent from 'webapp/components/custom-query/setup/sheet/loader';
import type { Args as BaseArgs } from 'webapp/components/custom-query/setup/wizard/common/step-common';
import CustomQuerySetupWizardCommonStepCommonComponent from 'webapp/components/custom-query/setup/wizard/common/step-common';
import CustomQuerySetupWizardSheetColumnPickerComponent from 'webapp/components/custom-query/setup/wizard/sheet/column-picker';
import type { SheetColumnOption } from 'webapp/components/custom-query/setup/wizard/sheet/column-picker';
import CustomQuerySetupWizardSheetControlsComponent from 'webapp/components/custom-query/setup/wizard/sheet/controls';
import FormErrorComponent from 'webapp/components/form-error';
import FormSelectComponent from 'webapp/components/form/select';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type CustomMetricSetupFormItemField from 'webapp/models-custom/custom-metric-setup-form/item/field';
import type CustomMetricSetupFormSheet from 'webapp/models-custom/custom-metric-setup-form/sheet';
import type DataSheet from 'webapp/models-custom/data-sheet';

export interface Args extends BaseArgs {
	form: CustomMetricSetupFormSheet;
	stepId: SheetColumnType;
}

// export interface CustomQuerySetupWizardSheetStepVerifyComponentInterface<T extends Args> {
// 	Args: T;
// 	Blocks: { default: [] }; // this is needed for yield
// }

export default abstract class CustomQuerySetupWizardSheetStepCommonComponent extends CustomQuerySetupWizardCommonStepCommonComponent<Args> {
	@tracked isReloadShown = false;

	@tracked selectedColumnId = '';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.selectInitialColumn();
	}

	get showSheetActions() {
		const source = this.args.model.sourceObj;
		return !source?.sourceType?.isFileUpload;
	}

	get a1Field(): CustomMetricSetupFormItemField | null {
		return this.args.form.getA1FieldForType(this.stepColumnType) || null;
	}

	get hasErrors(): boolean {
		const field = this.a1Field;
		return !!field && field.errorsArray.length > 0;
	}

	get isNextDisabled(): boolean {
		return this.hasErrors;
	}

	get stepColumnType(): SheetColumnType {
		return this.args.stepId;
	}

	get allSheets(): DataSheet[] {
		return this.args.form.sheets;
	}

	get selectedSheet(): DataSheet | null {
		return this.allSheets.find((item) => item.isActive) || null;
	}

	get selectedSheetId(): string {
		return this.selectedSheet?.id || '';
	}

	get showSheetDropdown(): boolean {
		return this.allSheets.length > 1;
	}

	get isNoneShown(): boolean {
		return [SheetColumnType.DIMENSION, SheetColumnType.DATE].includes(this.stepColumnType);
	}

	get isNoneDisabled(): boolean {
		return false;
	}

	get noneDisabledText(): string {
		return '';
	}

	get sheetOptions(): LabelValuePairString[] {
		return this.allSheets.map((sheet: DataSheet) => {
			return {
				value: sheet.id,
				label: sheet.id,
			};
		});
	}

	get columnOptions(): SheetColumnOption[] {
		const selectedSheet = this.selectedSheet;

		if (!selectedSheet) {
			return [];
		}

		const columns = Object.keys(selectedSheet.columnTypes).map((column) => {
			const columnData = selectedSheet.columnTypes[column]!,
				title = columnData.columnTitle,
				backupTitle = `Column ${column}`;
			return {
				value: `${selectedSheet.sheetPrefix}${column}:${column}`,
				label: title || backupTitle,
				label2: title ? backupTitle : '',
				type: columnData.type,
				typeText: columnData.type === SheetColumnType.METRIC ? 'Value' : columnData.type,
				isRecommended: columnData.type === this.stepColumnType, // for preselection
				isDisabled:
					this.stepColumnType !== SheetColumnType.DIMENSION && // all columns can be selected as dimension
					columnData.type !== this.stepColumnType,
			};
		});

		return columns;
	}

	selectInitialColumn(): void {
		let preselectedColumnId = '',
			preselectFirstValid = !this.args.isAlreadyVisited; // don't overwrite 'none' if we have selected it previously (going "back")

		const formField = this.a1Field;
		if (formField?.value) {
			preselectedColumnId = formField.value as string;

			// find the sheet the column is on
			const sheetSelection = SheetSelection.fromA1(preselectedColumnId);
			if (sheetSelection.isWholeCols) {
				const selectedSheet = this.allSheets.find(
					(item) => item.sheetPrefix === sheetSelection.a1Prefix,
				);
				if (selectedSheet) {
					this.selectSheet(selectedSheet.id, true);
				}
			} else {
				preselectFirstValid = true;
			}
		}

		if (!preselectedColumnId && preselectFirstValid) {
			// either nothing is selected or selection isn't whole column
			preselectedColumnId = this.getFirstValidColumn();
		}

		this.selectColumn(preselectedColumnId);
	}

	getFirstValidColumn(): string {
		let firstValidColumn = '';
		const firstColumn = this.columnOptions.find((item) => item.isRecommended);
		if (firstColumn) {
			firstValidColumn = firstColumn.value;
		}
		return firstValidColumn;
	}

	@action
	reloadSheetData(): void {
		const form = this.args.form;
		if (!form.isSheetDataReloading) {
			form.reloadData();
		}
	}

	@action
	showReload(): void {
		later(() => {
			this.isReloadShown = true;
		}, 1000);
	}

	@action
	selectSheet(sheetId: string, skipSelectingColumn = false): void {
		this.allSheets.forEach((s: DataSheet) => {
			s.isActive = s.id === sheetId;
		});
		if (!skipSelectingColumn) {
			this.selectColumn(this.getFirstValidColumn());
		}
	}

	@action
	selectColumn(columnId: AnySimpleValue): void {
		this.selectedColumnId = columnId as string;
		next(() => {
			// avoid same runloop issue
			const field = this.a1Field;
			if (field) {
				field.value = this.selectedColumnId;
				field.updateValueFormatted();
				void field.triggerValidate();
			}
		});
	}

	@action
	goNext(): void {
		this.submitInProgress = true;

		const validationPromise = this.a1Field?.validationPromise || Promise.resolve();

		void validationPromise.then(() => {
			this.submitInProgress = false;
			if (!this.hasErrors) {
				this.args.goNext();
			}
		});
	}
	<template>
		<div class="step-ct">

			<div class="top">
				<h3>{{this.stepTitle}}</h3>
				<p>
					{{{this.stepDescription}}}

					{{#if this.showSheetActions}}
						{{#if @form.fileUrl}}
							<br />
							<a
								class="o"
								href={{@form.fileUrl}}
								target="_blank"
								{{on "click" this.showReload}}
							>
								Modify data
								<CommonUiIconComponent @name="External" @size="xs" />
							</a>
							{{#if this.isReloadShown}}
								<a class="r" {{on "click" this.reloadSheetData}}>
									<CommonUiIconComponent @name="Refresh" @size="sm" />
									Refresh sheet data
								</a>
							{{/if}}
						{{/if}}
					{{/if}}
				</p>

				{{yield}}
				{{! .toggle-manual button }}
			</div>

			{{#if @form.isSheetDataReloading}}
				<CustomQuerySetupSheetLoaderComponent />
			{{else}}

				<FormErrorComponent @errors={{this.a1Field.errorsArray}} />

				{{#if this.a1Field.validationWarning}}
					<p class="warning">
						There might be an issue with your selection
						<ToolTipWithIconComponent
							@text={{this.a1Field.validationWarning}}
							@iconName="AlertFill"
							@maxWidth={{300}}
							@theme="orange"
						/>
					</p>
				{{/if}}

				{{#if this.showSheetDropdown}}
					<div class="row row--sheet">
						<FormSelectComponent
							name="sheet-id"
							@selected={{this.selectedSheetId}}
							@options={{this.sheetOptions}}
							@onChange={{this.selectSheet}}
							@placeholder="Select sheet"
							@isNumeric={{false}}
							@isRequired={{true}}
						/>
					</div>
				{{/if}}

				<CustomQuerySetupWizardSheetColumnPickerComponent
					@options={{this.columnOptions}}
					@selected={{this.selectedColumnId}}
					@isNoneShown={{this.isNoneShown}}
					@isNoneDisabled={{this.isNoneDisabled}}
					@noneDisabledText={{this.noneDisabledText}}
					@optionNoneLabel={{this.optionNoneLabel}}
					@select={{this.selectColumn}}
				/>

			{{/if}}

		</div>

		{{! controlsNode could still be unrendered }}
		{{#if @controlsNode}}
			{{#in-element @controlsNode}}
				<CustomQuerySetupWizardSheetControlsComponent
					@showBack={{true}}
					@isButtonDisabled={{this.isNextDisabled}}
					@submitInProgress={{this.submitInProgress}}
					@goNext={{this.goNext}}
					@goPrevious={{@goPrevious}}
				/>
			{{/in-element}}
		{{/if}}
	</template>
}
